import { ref } from 'vue'
import { getUnreadCount, getNotifications, markNotificationRead, markAllNotificationsRead } from '@/api/notification'
import { useGlobalToastCenter } from '@/composables/useGlobalToastCenter'

// 全局消息状态（跨组件共享）
const unreadCount = ref(0)
const messages = ref([])
let refreshTimer = null
const seenMessageIds = new Set()
const { pushToast, removeToastBySourceId, removeToastsBySource } = useGlobalToastCenter()

// 通知类型映射（用于 AppHeader 展示）
export const typeMap = {
  APPROVAL: 'approval',
  TASK: 'approval',
  SYSTEM: 'system',
  CUSTOM: 'system'
}

// 格式化时间
export function formatTime(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now - date

  if (diff < 60000) return '刚刚'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
  if (diff < 172800000) return '昨天'
  return date.toLocaleDateString('zh-CN')
}

function normalizeMessage(item) {
  return {
    id: item.id,
    type: typeMap[item.type] || 'system',
    rawType: item.type,
    title: item.title,
    content: item.summary || item.content || '',
    time: formatTime(item.createTime),
    isRead: item.read,
    priority: item.priority,
    createTime: item.createTime,
    linkType: item.linkType,
    businessId: item.businessId,
    webUrl: item.webUrl,
    webPath: item.webPath
  }
}

// 加载未读数量
export async function loadUnreadCount() {
  try {
    const count = await getUnreadCount()
    unreadCount.value = count || 0
  } catch (error) {
    console.error('获取未读数量失败:', error)
  }
}

// 加载最近消息（未读）
export async function loadRecentMessages() {
  try {
    const res = await getNotifications({ pageNum: 1, pageSize: 30, status: 0 })
    const normalized = (res.records || []).map(normalizeMessage)
    messages.value = normalized.slice(0, 10)

    normalized
      .filter(item => item.rawType !== 'APPROVAL' && item.rawType !== 'TASK')
      .forEach(item => {
        if (seenMessageIds.has(item.id)) return

        seenMessageIds.add(item.id)
        pushToast({
          id: `message:${item.id}`,
          source: 'message',
          rawId: item.id,
          title: item.title || '消息通知',
          content: item.content || '',
          time: item.createTime || '',
          sortTime: item.createTime || '',
          variant: item.type === 'system' ? 'system' : 'message',
          actions: ['view'],
          payload: item
        })
      })
  } catch (error) {
    console.error('加载消息列表失败:', error)
  }
}

// 标记单条已读（包含 API 调用 + 状态更新 + 数据重载）
export async function markAsRead(id) {
  try {
    await markNotificationRead(id)
    removeToastBySourceId('message', id)
    seenMessageIds.delete(id)
    const msg = messages.value.find(m => m.id === id)
    if (msg && !msg.isRead) {
      msg.isRead = true
    }
    if (unreadCount.value > 0) {
      unreadCount.value--
    }
    // 重新加载消息列表以保持同步
    await loadRecentMessages()
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

// 标记全部已读（包含 API 调用 + 状态更新 + 数据重载）
export async function markAllRead() {
  try {
    await markAllNotificationsRead()
    removeToastsBySource('message')
    seenMessageIds.clear()
    messages.value.forEach(m => m.isRead = true)
    unreadCount.value = 0
    // 重新加载以保持同步
    await loadRecentMessages()
    await loadUnreadCount()
  } catch (error) {
    console.error('标记全部已读失败:', error)
  }
}

// 删除消息
export function deleteMessage(id) {
  const index = messages.value.findIndex(m => m.id === id)
  if (index > -1) {
    const msg = messages.value[index]
    // 如果是未读消息，减少未读数量
    if (!msg.isRead && unreadCount.value > 0) {
      unreadCount.value--
    }
    messages.value.splice(index, 1)
  }
  removeToastBySourceId('message', id)
  seenMessageIds.delete(id)
}

// 启动定时刷新
export function startRefresh(interval = 60000) {
  stopRefresh()
  refreshTimer = setInterval(() => {
    loadUnreadCount()
    loadRecentMessages()
  }, interval)
}

// 停止定时刷新
export function stopRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
  seenMessageIds.clear()
}

// 初始化
export async function initMessageStore() {
  await loadUnreadCount()
  await loadRecentMessages()
  startRefresh()
}

// 获取全局状态
export function useMessageStore() {
  return {
    unreadCount,
    messages,
    loadUnreadCount,
    loadRecentMessages,
    markAsRead,
    markAllRead,
    deleteMessage,
    startRefresh,
    stopRefresh,
    initMessageStore
  }
}
